<div class="ws-page laris-loc-page">
  <header class="page-head">
    <div>
      <p class="kicker">Building enforcement</p>
      <div class="page-head-title-row">
        <app-page-back-btn />
        <div class="page-head-title-row__heading">
          <h2 *ngIf="hasSelectedLocation() && selectedLocation() as loc; else lidarTitleDefault">
            LiDAR Survey — {{ loc.name }}
          </h2>
          <ng-template #lidarTitleDefault><h2>LiDAR Survey</h2></ng-template>
        </div>
        <app-page-refresh-btn [loading]="loading()" (refresh)="reload()" />
      </div>
      <p class="loc-page-lead" *ngIf="hasSelectedLocation() && selectedLocation() as loc">
        Upload and process scans for <strong>{{ loc.code }}</strong>. Buildings are matched to registered plot boundaries under this location.
      </p>
      <p class="loc-page-lead" *ngIf="!hasSelectedLocation()">
        Upload drone and vehicle scans at any location level — authority, zone, sector, or plot.
        Buildings are detected from the point cloud and matched to registered plot boundaries.
      </p>
    </div>
    <div class="actions">
      <button type="button" class="btn primary btn-compact" *ngIf="canManageLocations()" (click)="openUploadScan()">
        Upload LiDAR
      </button>
      <button type="button" class="btn secondary btn-compact" *ngIf="hasSelectedLocation()" (click)="clearLocationSelection()">
        Clear selection
      </button>
      <a routerLink="/enforcement/areas" class="btn secondary btn-compact">Locations</a>
    </div>
  </header>

  <p class="toast loc-toast" *ngIf="toast()">{{ toast() }}</p>
  <p class="error" *ngIf="error()">{{ error() }}</p>

  <p class="loc-loading" *ngIf="loading()">Loading locations…</p>

  <section class="page-card loc-tab-panel" *ngIf="!loading() && locationId() != null && !hasSelectedLocation()">
    <div class="loc-empty-panel">
      <h3 class="loc-panel-title">Location not found</h3>
      <p>Open a file from the list or browse <a routerLink="/enforcement/areas">Locations</a>.</p>
    </div>
  </section>

  <section class="page-card loc-tab-panel" *ngIf="!loading() && hasSelectedLocation() && locationId() as lid">
    <app-location-lidar-panel
      [locationId]="lid"
      [locations]="locations()"
      (toastMessage)="showToast($event)"
      (scansChanged)="loadAllScanJobs()"
    />
  </section>

  <section class="page-card loc-tab-panel lidar-main-panel" *ngIf="!loading() && !focusLocation()">
    <header class="lidar-uploads-head">
      <div>
        <h3 class="loc-panel-title">Uploaded LiDAR files</h3>
        <p class="loc-panel-sub lidar-main-tab-lead">All drone and vehicle scans uploaded at any location level.</p>
      </div>
      <span class="lidar-uploads-count">{{ allUploads().length }} file{{ allUploads().length === 1 ? '' : 's' }}</span>
    </header>

    <p class="loc-loading" *ngIf="jobsLoading()">Loading uploaded files…</p>

    <p class="lidar-uploads-empty" *ngIf="!jobsLoading() && !allUploads().length">
      No LiDAR files uploaded yet.
      <button type="button" class="btn-link" *ngIf="canManageLocations()" (click)="openUploadScan()">Upload LiDAR</button>
    </p>

    <div class="lidar-uploads-table-wrap" *ngIf="!jobsLoading() && allUploads().length">
      <table class="lidar-uploads-table">
        <thead>
          <tr>
            <th>File</th>
            <th>Type</th>
            <th>Location</th>
            <th>Status</th>
            <th>Uploaded</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of allUploads()">
            <td class="lidar-uploads-file">{{ fileNameFromUri(row.job.rawStorageUri) }}</td>
            <td>{{ scanTypeLabel(row.job.scanType) }}</td>
            <td>
              <span class="lidar-uploads-loc-name">{{ row.locationName }}</span>
              <span class="lidar-uploads-loc-meta">{{ row.locationTypeCode }} · {{ row.locationCode }}</span>
            </td>
            <td>
              <span class="loc-status-pill" [ngClass]="scanStatusClass(row.job.statusCd)">
                {{ formatScanStatus(row.job.statusCd) }}
              </span>
            </td>
            <td>{{ formatUploadDate(row.job.createdAt) }}</td>
            <td class="lidar-uploads-action">
              <button type="button" class="btn-link" *ngIf="row.locationId != null" (click)="openUploadRow(row)">
                Open
              </button>
              <button
                type="button"
                class="btn-link lidar-delete-link"
                *ngIf="canManageLocations()"
                [disabled]="!canDeleteScan(row.job) || deleteImpactLoadingId() === row.job.id || deletingScanId() === row.job.id"
                (click)="requestDeleteScan(row.job)"
              >
                {{
                  deletingScanId() === row.job.id
                    ? 'Deleting…'
                    : deleteImpactLoadingId() === row.job.id
                      ? 'Checking…'
                      : 'Delete'
                }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</div>

<div class="laris-overlay" *ngIf="uploadScanOpen()" (click)="closeUploadScan()">
  <div class="laris-modal" role="dialog" aria-modal="true" (click)="$event.stopPropagation()">
    <header class="laris-modal-head"><h3>Upload LiDAR scan</h3></header>
    <div class="laris-modal-body">
      <p class="laris-modal-form-error" *ngIf="uploadFormErrors().form" role="alert">
        {{ uploadFormErrors().form }}
      </p>
      <label class="laris-field">
        Survey type
        <select [(ngModel)]="scanUploadForm.scanType">
          <option value="drone">Drone</option>
          <option value="vehicle">Vehicle</option>
        </select>
      </label>
      <label class="laris-field" [class.laris-field--invalid]="uploadFormErrors().file">
        File
        <input type="file" accept=".las,.laz,.LAS,.LAZ" (change)="onScanFileSelected($event)" />
        <span class="ws-field-error" *ngIf="uploadFormErrors().file">{{ uploadFormErrors().file }}</span>
      </label>
      <div class="lidar-upload-loc-picker" *ngIf="!hasSelectedLocation()" [class.laris-field--invalid]="uploadFormErrors().location">
        <span class="lidar-upload-loc-label">Location</span>
        <p class="lidar-upload-tree-hint">Expand the tree and click a zone, sector, or plot to select.</p>
        <div class="loc-tree lidar-upload-tree" role="tree" aria-label="Select upload location">
          <ng-container *ngFor="let node of uploadLocationTree()">
            <ng-container *ngTemplateOutlet="uploadTreeNode; context: { $implicit: node, depth: 0 }"></ng-container>
          </ng-container>
        </div>
        <p class="lidar-upload-selected" *ngIf="scanUploadForm.locationId as selectedId">
          Selected: <strong>{{ selectedUploadLocationLabel(selectedId) }}</strong>
        </p>
        <span class="ws-field-error" *ngIf="uploadFormErrors().location">{{ uploadFormErrors().location }}</span>
      </div>
      <p class="lidar-upload-loc-note" *ngIf="hasSelectedLocation() && selectedLocation() as loc">
        Uploading to <strong>{{ loc.name }}</strong> ({{ loc.code }})
      </p>
    </div>
    <footer class="laris-modal-foot">
      <button type="button" class="btn secondary" (click)="closeUploadScan()">Cancel</button>
      <button type="button" class="btn primary" (click)="submitUploadScan()" [disabled]="saving()">Upload</button>
    </footer>
  </div>
</div>

<ng-template #uploadTreeNode let-node let-depth="depth">
  <div
    class="loc-tree-node"
    role="treeitem"
    [attr.aria-expanded]="node.children.length ? isUploadTreeExpanded(node.location.id) : null"
    [attr.aria-level]="depth + 1"
    [attr.aria-selected]="isUploadLocationSelected(node.location.id)"
  >
    <div
      class="loc-tree-row"
      [class.loc-tree-row--selected]="isUploadLocationSelected(node.location.id)"
      [style.--tree-depth]="depth"
    >
      <button
        type="button"
        class="loc-tree-toggle"
        *ngIf="node.children.length; else uploadTreeSpacer"
        [attr.aria-label]="isUploadTreeExpanded(node.location.id) ? 'Collapse' : 'Expand'"
        (click)="toggleUploadTreeExpand(node.location.id, $event)"
      >
        <span class="loc-tree-chevron" [class.loc-tree-chevron--open]="isUploadTreeExpanded(node.location.id)"></span>
      </button>
      <ng-template #uploadTreeSpacer><span class="loc-tree-spacer" aria-hidden="true"></span></ng-template>

      <button
        type="button"
        class="loc-tree-label"
        [class.loc-tree-label--disabled]="!isUploadLocationSelectable(node.location)"
        (click)="onUploadTreeLabelClick(node)"
      >
        <span class="loc-type-icon loc-type-icon--sm" [ngClass]="'loc-type-icon--' + typeTone(node.location.locationTypeName)">
          {{ typeInitial(node.location.locationTypeName) }}
        </span>
        <span class="loc-tree-text">
          <span class="loc-tree-name">{{ node.location.name }}</span>
          <span class="loc-tree-meta">{{ node.location.code }} · {{ node.location.locationTypeName }}</span>
        </span>
        <span class="loc-tree-count" *ngIf="node.children.length">{{ node.children.length }}</span>
      </button>
    </div>

    <div
      class="loc-tree-children"
      *ngIf="node.children.length && isUploadTreeExpanded(node.location.id)"
      [style.--tree-depth]="depth"
    >
      <ng-container *ngFor="let child of node.children">
        <ng-container *ngTemplateOutlet="uploadTreeNode; context: { $implicit: child, depth: depth + 1 }"></ng-container>
      </ng-container>
    </div>
  </div>
</ng-template>

<app-confirm-dialog
  [open]="deleteConfirmOpen()"
  title="Delete LiDAR file?"
  [message]="deleteConfirmMessage()"
  confirmLabel="Delete"
  cancelLabel="Cancel"
  [destructive]="true"
  (confirm)="confirmDeleteScan()"
  (cancel)="cancelDeleteScan()"
/>
